// store/student/exam/store/_saga.js
import { call, put, takeLatest, debounce } from 'redux-saga/effects';
import * as types from './_actionTypes';
import * as actions from './_actions';
import StudentExamHelper from '../../../../utils/API/BackendHelper/student/exam/exam';

// Get available exams
function* getAvailableExamsSaga() {
  try {
    const response = yield call([StudentExamHelper, 'getAvailableExams']);
    if (response.success) {
      yield put(actions.getAvailableExamsSuccess(response.data));
    } else {
      yield put(actions.getAvailableExamsFailure(response.message));
    }
  } catch (error) {
    yield put(actions.getAvailableExamsFailure(error.message));
  }
}

// Start exam
function* startExamSaga(action) {
  try {
    console.log('Starting exam with action:', action);
    const { examId } = action.payload;
    console.log('Exam ID:', examId);
    const response = yield call([StudentExamHelper, 'startExam'], examId);
    
    if (response.success) {
      yield put(actions.startExamSuccess(response.data));
      
      // Set timer from backend response if available
      if (response.data.attempt && response.data.attempt.time_remaining !== undefined) {
        yield put(actions.setExamTimer(response.data.attempt.time_remaining));
      } else if (response.data.attempt) {
        // Fallback: Calculate time remaining on frontend
        const examEnd = new Date(response.data.attempt.exam_window_end);
        const now = new Date();
        const timeRemaining = Math.max(0, Math.floor((examEnd - now) / 1000));
        yield put(actions.setExamTimer(timeRemaining));
      }
    } else {
      // Check if it's an expired exam with redirect
      if (response.redirect) {
        yield put(actions.startExamFailure(response.message));
        // Navigate to results page
        if (typeof window !== 'undefined') {
          window.location.href = response.redirect;
        }
      } else {
        yield put(actions.startExamFailure(response.message));
      }
    }
  } catch (error) {
    yield put(actions.startExamFailure(error.message));
  }
}

// Save answer (with debounce for auto-save)
function* saveAnswerSaga(action) {
  try {
    const { attemptId, questionId, selectedAnswer } = action.payload;
    const response = yield call(
      [StudentExamHelper, 'saveAnswer'],
      attemptId,
      questionId,
      selectedAnswer
    );
    
    if (response.success) {
      yield put(actions.saveAnswerSuccess(response.data));
      
      // Reset auto-save status after 2 seconds
      yield call(delay, 2000);
      yield put(actions.setAutoSaveStatus('idle'));
    } else {
      // Handle auto-submit if exam time expired
      if (response.should_auto_submit) {
        yield put(actions.submitExam(attemptId));
      } else {
        yield put(actions.saveAnswerFailure(response.message));
      }
    }
  } catch (error) {
    yield put(actions.saveAnswerFailure(error.message));
  }
}

// Submit exam
function* submitExamSaga(action) {
  try {
    const { attemptId } = action.payload;
    const response = yield call([StudentExamHelper, 'submitExam'], attemptId);
    
    if (response.success) {
      yield put(actions.submitExamSuccess(response.data));
    } else {
      yield put(actions.submitExamFailure(response.message));
    }
  } catch (error) {
    yield put(actions.submitExamFailure(error.message));
  }
}

// Get exam results
function* getExamResultsSaga(action) {
  try {
    const { attemptId } = action.payload;
    const response = yield call([StudentExamHelper, 'getExamResults'], attemptId);
    
    if (response.success) {
      yield put(actions.getExamResultsSuccess(response.data));
    } else {
      yield put(actions.getExamResultsFailure(response.message));
    }
  } catch (error) {
    yield put(actions.getExamResultsFailure(error.message));
  }
}

// Get exam history
function* getExamHistorySaga() {
  try {
    const response = yield call([StudentExamHelper, 'getExamHistory']);
    
    if (response.success) {
      yield put(actions.getExamHistorySuccess(response.data));
    } else {
      yield put(actions.getExamHistoryFailure(response.message));
    }
  } catch (error) {
    yield put(actions.getExamHistoryFailure(error.message));
  }
}

// Helper function for delay
function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Watcher saga
export default function* examSaga() {
  yield takeLatest(types.GET_AVAILABLE_EXAMS_REQUEST, getAvailableExamsSaga);
  yield takeLatest(types.START_EXAM_REQUEST, startExamSaga);
  yield debounce(1000, types.SAVE_ANSWER_REQUEST, saveAnswerSaga); // Debounce auto-save by 1 second
  yield takeLatest(types.SUBMIT_EXAM_REQUEST, submitExamSaga);
  yield takeLatest(types.GET_EXAM_RESULTS_REQUEST, getExamResultsSaga);
  yield takeLatest(types.GET_EXAM_HISTORY_REQUEST, getExamHistorySaga);
}